import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { getUsers } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import SearchInput from '@/components/common/SearchInput';
import Select from '@/components/common/Select';
import { StatusBadge } from '@/components/common/Badge';
import { formatDate, formatTimeAgo } from '@/utils/formatters';
import type { UserWithStatus } from '@/types/user';

export default function Users() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search, statusFilter],
    queryFn: () => getUsers(page, 20, search || undefined, statusFilter),
  });

  const columns = useMemo<ColumnDef<UserWithStatus>[]>(
    () => [
      {
        accessorKey: 'full_name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
              {row.original.full_name?.[0]?.toUpperCase() || row.original.email[0].toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900">{row.original.full_name || 'No name'}</p>
              <p className="text-xs text-gray-500">{row.original.email}</p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'phone_number',
        header: 'Phone',
        cell: ({ row }) => row.original.phone_number || '-',
      },
      {
        accessorKey: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const city = row.original.city;
          const state = row.original.state;
          if (!city && !state) return '-';
          return `${city || ''}${city && state ? ', ' : ''}${state || ''}`;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.account_status?.status || 'active';
          return <StatusBadge status={status} />;
        },
      },
      {
        accessorKey: 'is_verified',
        header: 'Verified',
        cell: ({ row }) => (
          <span className={row.original.is_verified ? 'text-green-600' : 'text-gray-400'}>
            {row.original.is_verified ? '✓' : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'last_active',
        header: 'Last Active',
        cell: ({ row }) => row.original.last_active ? formatTimeAgo(row.original.last_active) : 'Never',
      },
      {
        accessorKey: 'created_at',
        header: 'Joined',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
    ],
    []
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'banned', label: 'Banned' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts and view activity
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name, email, or phone..."
              className="flex-1"
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              className="w-full sm:w-48"
            />
          </div>
        </CardBody>
      </Card>

      {/* Users table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.users || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
            onRowClick={(user) => navigate(`/users/${user.user_id}`)}
          />
        </CardBody>
      </Card>
    </div>
  );
}
